import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import {
  contactSchema,
  type ContactPayload,
} from "../../../shared/contactSchema";
import "./style.css";

type FormStatus = "idle" | "submitting" | "success" | "error";

type ContactFormProps = {
  className?: string;
  fieldsReady?: boolean;
  enableTabIndex?: boolean;
  showButton?: boolean;
  buttonText?: string;
  buttonType?: "submit" | "button";
  onButtonClick?: () => void;
  buttonClassName?: string;
  buttonFading?: boolean;
};

export const ContactForm = ({
  className = "",
  fieldsReady = true,
  enableTabIndex = true,
  showButton = true,
  buttonText = "SEND MESSAGE",
  buttonType = "submit",
  onButtonClick,
  buttonClassName = "",
  buttonFading = false,
}: ContactFormProps) => {
  const tabIndex = enableTabIndex ? 0 : -1;
  const [status, setStatus] = useState<FormStatus>("idle");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactPayload>({
    resolver: zodResolver(contactSchema),
    mode: "onTouched",
  });

  const onSubmit = async (data: ContactPayload) => {
    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className={`contact-form ${className}`}>
        <div className="contact-form__success">
          <span className="contact-form__success-icon" aria-hidden="true">
            <svg
              width="20"
              height="15"
              viewBox="0 0 20 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 7.5L7.5 13L18 2"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <p className="contact-form__success-headline">Message sent</p>
          <p className="contact-form__success-sub">
            I'll get back to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`contact-form ${className}`}>
      {/* Honeypot — hidden from real users */}
      <input
        type="text"
        {...register("website")}
        className="contact-form__hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className={`contact-form__fields${fieldsReady ? " is-ready" : ""}`}>
        <label className="contact-form__field">
          <span className="contact-form__label">Name</span>
          <input
            className={`contact-form__input${errors.name ? " is-invalid" : ""}`}
            type="text"
            autoComplete="name"
            tabIndex={tabIndex}
            {...register("name")}
          />
          {errors.name && (
            <span className="contact-form__error">{errors.name.message}</span>
          )}
        </label>
        <label className="contact-form__field">
          <span className="contact-form__label">E-mail</span>
          <input
            className={`contact-form__input${errors.email ? " is-invalid" : ""}`}
            type="email"
            autoComplete="email"
            tabIndex={tabIndex}
            {...register("email")}
          />
          {errors.email && (
            <span className="contact-form__error">{errors.email.message}</span>
          )}
        </label>
        <label className="contact-form__field">
          <span className="contact-form__label">
            Company <span className="contact-form__optional">(optional)</span>
          </span>
          <input
            className={`contact-form__input${errors.company ? " is-invalid" : ""}`}
            type="text"
            autoComplete="organization"
            tabIndex={tabIndex}
            {...register("company")}
          />
        </label>
        <label className="contact-form__field">
          <span className="contact-form__label">Message</span>
          <textarea
            className={`contact-form__input contact-form__textarea${errors.message ? " is-invalid" : ""}`}
            tabIndex={tabIndex}
            {...register("message")}
          />
          {errors.message && (
            <span className="contact-form__error">
              {errors.message.message}
            </span>
          )}
        </label>
      </div>

      {status === "error" && (
        <p className="contact-form__status contact-form__status--error">
          Something went wrong — please try again.
        </p>
      )}

      {showButton && (
        <button
          type="button"
          className={`contact-form__button ${buttonClassName}${buttonFading ? " is-fading" : ""}`}
          onClick={buttonType === "button" ? onButtonClick : () => handleSubmit(onSubmit)()}
          disabled={status === "submitting"}
        >
          {status === "submitting" ? "Sending\u2026" : buttonText}
        </button>
      )}
    </div>
  );
};
